import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useLocation } from 'react-router'
import { AnalyticsLoader, RenderCharts } from '../../Dashboard'
import { Wrapper } from '../../Dashboard/styles'
import { DashboardRecord } from '@base'
import { Devices, Spacings } from '@styles'
import { LocalMallOutlined, PeopleAltOutlined, ListAlt, AccountBalance as AccountBalanceIcon, AttachMoney } from '@material-ui/icons'
import { isSuperAdmin, isSupplyAdmin } from 'src/utils/adminFilter'
import { CardDetail } from './CompanyReport'
import { decimalSeparatedNumber, displayAmount, processData, processDataForPieChart } from '@utils'
import { getLastTwoMonthsData } from '@base/composite/DashboardRecord'
import { useSelector } from 'react-redux'
import { getCompanyData, getManagerDashboardData } from '@data'
import { strings } from '@constants'

const MainWrapper = styled.div`
	display: grid;
	width: 100%;
	grid-template-columns: repeat(1, 1fr);

	@media ${Devices.tablet} {
		grid-template-columns: repeat(2, 2fr);
		grid-column-gap: ${Spacings.SPACING_4B};
	}
	@media ${Devices.laptop} {
		grid-template-columns: repeat(4, 4fr);
	}
`
const Dashboard = () => {
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const [records, SetRecord] = useState()
	const [chartStats, setChartStats] = useState([])
	const { pathname, search } = useLocation()

	const convetWithYear = (arrayData) => {
		if (arrayData?.data.length > 12) {
			const data = arrayData?.data?.map((item) => {
				const _item = item?.label.split(' ')
				if (!_item[0]?.includes('Apr')) {
					return { ...item, label: _item[0] }
				}
				return item
			})
			return { ...arrayData, data }
		}

		return arrayData
	}

	const [accessType, numMonths, employeeId] = useMemo(() => {
		const params = new URLSearchParams(search)
		let _accessType = params?.get('access_type') ?? 'OVERALL'
		let _numMonths = params?.get('numMonths') ?? 6
		let _employeeId = params?.get('employee_id') ?? null
		return [_accessType, _numMonths, _employeeId]
	}, [search])
	let newUsersCount = getLastTwoMonthsData(records?.total_users, 'unit', { month: numMonths })

	const createChartStatsData = (data) => {
		const newData = []
		if (data.total_enquiries) {
			const barChartData = data?.total_serviced_weight
				? [
						{
							data: data.total_enquiries,
							key: 'enquiry',
						},
						{
							data: data.total_orders,
							key: 'order',
						},
						{
							data: data?.total_serviced_weight,
							key: 'serviced_weight',
						},
				  ]
				: [
						{
							data: data.total_enquiries,
							key: 'enquiry',
						},
						{
							data: data.total_orders,
							key: 'order',
						},
				  ]
			newData.push(
				convetWithYear(
					processData(
						barChartData,
						{
							dataKey: 'weight',
							valueDivisor: 1000, // for converting to MT
							yLabel: 'Weight in MT',
							unit: 'MT',
							formatter: decimalSeparatedNumber,
							header: strings('monthly_conversion_chart'),
							chartType: 'bar',
						},
						numMonths === 'OVERALL' ? data.total_enquiries?.length : numMonths
					)
				)
			)
		}
		if (data.total_supplier_quote) {
			newData.push(
				convetWithYear(
					processData(
						[
							{
								data: data.total_supplier_quote,
								key: 'quote',
							},
							{
								data: data.total_orders,
								key: 'converted_order',
							},
						],
						{
							dataKey: 'weight',
							valueDivisor: 1000, // for converting to MT
							yLabel: 'Weight in MT',
							unit: 'MT',
							formatter: decimalSeparatedNumber,
							header: strings('monthly_supply_chart'),
							chartType: 'bar',
						},
						numMonths === 'OVERALL' ? data?.total_supplier_quote?.length : numMonths
					)
				)
			)
		}
		if (data?.total_purchase_business) {
			newData.push(
				convetWithYear(
					processData(
						[
							{
								data: data.total_purchase_business,
								key: 'purchase',
							},
							{
								data: data.total_sale_business,
								key: 'sale',
							},
						],
						{
							dataKey: 'amount',
							valueDivisor: 100000, // for converting to lakh
							yLabel: 'Amount in Lakh',
							unit: 'Lakh',
							formatter: displayAmount,
							header: 'Monthly sale / purchase comparison',
							chartType: 'bar',
						},
						numMonths === 'OVERALL' ? data?.total_purchase_business?.length : numMonths
					)
				)
			)
		}
		if (data?.total_enquiries) {
			newData.push(
				convetWithYear(
					processData(
						[
							{
								data: data.total_enquiries,
								key: 'enquiry',
							},
						],
						{
							dataKey: 'weight',
							valueDivisor: 1000, // for converting to MT
							yLabel: 'Weight in MT',
							unit: 'MT',
							formatter: decimalSeparatedNumber,
							header: strings('enquiries'),
							chartType: 'line',
							summary: [
								{
									getterKey: 'enquiry',
									label: 'Total:',
									key: 'unit',
								},
								{
									getterKey: 'enquiry',
									label: 'Weight:',
									key: 'weight',
									formatter: decimalSeparatedNumber,
									valueDivisor: 1000,
									unit: 'MT',
								},
							],
						},
						numMonths === 'OVERALL' ? data?.total_enquiries?.length : numMonths
					)
				)
			)
		}

		if (data?.total_orders) {
			newData.push(
				convetWithYear(
					processData(
						[
							{
								data: data.total_orders,
								key: 'order',
							},
						],
						{
							dataKey: 'weight',
							valueDivisor: 1000, // for converting to MT
							yLabel: 'Weight in MT',
							unit: 'MT',
							formatter: decimalSeparatedNumber,
							header: strings('Orders'),
							chartType: 'line',
							summary: [
								{
									getterKey: 'order',
									label: 'Total:',
									key: 'unit',
								},
								{
									getterKey: 'order',
									label: 'Weight:',
									key: 'weight',
									formatter: decimalSeparatedNumber,
									valueDivisor: 1000,
									unit: 'MT',
								},
							],
						},
						numMonths === 'OVERALL' ? data?.total_orders?.length : numMonths
					)
				)
			)
		}

		if (data?.sku_category_wise_data) {
			newData.push(
				processDataForPieChart(
					{
						data: data.sku_category_wise_data,
					},
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: 'Weight in MT',
						unit: 'MT',
						formatter: (value) => decimalSeparatedNumber(value / 1000),
						header: 'Enquired weight per SKU (category)',
						chartType: 'pie',
						labelKey: 'category_name',
					},
					numMonths === 'OVERALL' ? data?.sku_category_wise_data?.length : numMonths
				)
			)
		}

		if (data?.order_skuwise_data) {
			newData.push(
				processDataForPieChart(
					{
						data: data.order_skuwise_data,
					},
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: 'Weight in MT',
						unit: 'MT',
						formatter: (value) => decimalSeparatedNumber(value / 1000),
						header: 'Ordered weight per SKU (category)',
						chartType: 'pie',
						labelKey: 'category_name',
					},
					numMonths === 'OVERALL' ? data?.order_skuwise_data?.length : numMonths
				)
			)
		}
		setChartStats(newData)
	}
	useEffect(() => {
		if (isSuperAdmin(user.role)) {
			getCompanyData(employeeId === 'ALL' ? null : employeeId, accessType, numMonths === 'OVERALL' ? 0 : numMonths)
				.then((data) => {
					SetRecord(data)
					createChartStatsData(data)
				})
				.catch((err) => {})
		} else {
			getManagerDashboardData(user.user_id, accessType === 'SELF' ? user.user_id : null)
				.then((data) => {
					SetRecord(data)
					createChartStatsData(data)
				})
				.catch((err) => {})
		}
	}, [accessType, numMonths, employeeId, search])
	const CardsUI = () => {
		const selectMonth = { month: numMonths }
		if (!user.role) return '-'
		if (isSupplyAdmin(user.role, false)) {
			return (
				<MainWrapper>
					<DashboardRecord
						title={'Total Enquiry'}
						initialData={records?.total_enquiries}
						key={'total_enquiries'}
						keyName={'weight'}
						icon={<ListAlt fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Order'}
						initialData={records?.total_orders}
						key={'total_orders'}
						keyName={'weight'}
						icon={<LocalMallOutlined fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Quotes'}
						initialData={records?.total_supplier_quote}
						key={'weight'}
						keyName={'weight'}
						icon={<PeopleAltOutlined fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Quotes Converted'}
						initialData={records?.total_supplier_quote_converted}
						key={'weight'}
						keyName={'weight'}
						icon={<PeopleAltOutlined fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Sale Invoices'}
						initialData={records?.total_sale_business}
						key={'total_sale_business'}
						keyName={'amount'}
						icon={<AccountBalanceIcon fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Purchase Invoices'}
						initialData={records?.total_purchase_business}
						key={'total_purchase_business'}
						keyName={'amount'}
						icon={<AccountBalanceIcon fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Users'}
						initialData={records?.total_users}
						key={'unit'}
						keyName={'unit'}
						icon={<PeopleAltOutlined fontSize='medium' />}
					/>
				</MainWrapper>
			)
		} else {
			return (
				<MainWrapper>
					<DashboardRecord
						title={'Total Enquiry'}
						initialData={records?.total_enquiries}
						key={'total_enquiries'}
						keyName={'weight'}
						icon={<ListAlt fontSize='medium' />}
						selectMonth={selectMonth}
						extraData={`(${newUsersCount[0] ? newUsersCount[0] : '0'} New Users)`}
					/>
					<DashboardRecord
						title={'Total Order'}
						initialData={records?.total_orders}
						key={'total_orders'}
						keyName={'weight'}
						icon={<LocalMallOutlined fontSize='medium' />}
						selectMonth={selectMonth}
					/>
					<DashboardRecord
						title={'Total Invoices / Monthly Target (in Crores)'}
						initialData={records?.total_sale_business}
						key={'total_sale_business'}
						keyName={'amount'}
						icon={<AccountBalanceIcon fontSize='medium' />}
						selectMonth={selectMonth}
						extraData={`/ ${displayAmount(records?.monthly_target)}`}
						totalValue={records?.monthly_target}
					/>
					<CardDetail
						heading={'Payments Received / Expected (in Crores)'}
						value={`${displayAmount(records?.payment_received / 10000000)} / ${displayAmount(
							records?.payment_expected_today / 10000000
						)}`}
						icon={<AttachMoney fontSize='medium' />}
						hasDefinition={false}
					/>
				</MainWrapper>
			)
		}
	}
	return (
		<Wrapper>
			{CardsUI()}
			<AnalyticsLoader data={chartStats} />
			<Wrapper fullWidth={numMonths === 'OVERALL'}>
				<RenderCharts data={chartStats} vertical={numMonths === 'OVERALL'} />
			</Wrapper>
		</Wrapper>
	)
}

export default Dashboard
